import { useState } from 'react';
import { quantraApi, PRODUCT_TYPES } from '../lib/quantra-api';
import { getSampleRequest } from '../lib/sample-requests';
import { useAuth } from '../hooks/useAuth';

export default function PricingPlayground() {
  const dashboardProducts = PRODUCT_TYPES.filter((p) => p.id !== 'cds');
  const [selectedProduct, setSelectedProduct] = useState(dashboardProducts[0] as typeof PRODUCT_TYPES[number]);
  const [request, setRequest] = useState(getSampleRequest(dashboardProducts[0].id));
  const [response, setResponse] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const { user, login } = useAuth();
  
  const handleProductChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const product = dashboardProducts.find(p => p.id === e.target.value);
    if (product) {
      setSelectedProduct(product);
      setRequest(getSampleRequest(product.id));
      setResponse(null);
      setError(null);
      setDuration(null);
    }
  };
  
  const runPricing = async () => {
    if (!user) {
      const signedInUser = await login();
      if (!signedInUser) {
        setError('Sign in is required to run pricing calls.');
        return;
      }
    }
    setLoading(true);
    setError(null);
    setResponse(null);
    setDuration(null);
    
    try {
      const parsedRequest = JSON.parse(request);
      const result = await quantraApi.price(selectedProduct.endpoint, parsedRequest);
      
      setDuration(Math.round(result.duration_ms));
      
      if (result.success) {
        setResponse(JSON.stringify(result.data, null, 2));
      } else {
        setError(result.error || 'Pricing failed');
      }
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError('Invalid JSON in request');
      } else {
        setError(err instanceof Error ? err.message : 'Unknown error');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const formatJson = () => {
    try {
      const parsed = JSON.parse(request);
      setRequest(JSON.stringify(parsed, null, 2));
    } catch {
      // Invalid JSON
    }
  };
  
  return (
    <div className="bg-white border border-[#e5e5e5] rounded-xl p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#f5f5f5] flex items-center justify-center">
            <svg className="w-4 h-4 text-[#8a6a2f]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-[#0a0a0a]">Pricing Playground</h2>
        </div>
        
        <select 
          value={selectedProduct.id}
          onChange={handleProductChange}
          className="px-3 py-1.5 bg-white border border-[#d4d4d4] rounded-lg text-sm text-[#0a0a0a] focus:outline-none focus:border-[#8a6a2f] cursor-pointer"
        >
          {dashboardProducts.map(product => (
            <option key={product.id} value={product.id}>{product.name}</option>
          ))}
        </select>
      </div>
      
      {/* Request/Response */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Request */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-[#737373] uppercase tracking-wide font-medium">Request</label>
            <div className="flex items-center gap-3">
              <button onClick={formatJson} className="text-xs text-[#737373] hover:text-[#0a0a0a] transition-colors">
                Format
              </button>
              <span className="text-xs text-[#a3a3a3] font-mono">
                POST {selectedProduct.endpoint}
              </span>
            </div>
          </div>
          <div className="bg-[#171717] rounded-xl overflow-hidden">
            <textarea
              value={request}
              onChange={e => setRequest(e.target.value)}
              className="w-full h-72 px-4 py-3 bg-transparent text-xs font-mono text-[#d4d4d4] focus:outline-none resize-none"
              spellCheck={false}
            />
          </div>
        </div>
        
        {/* Response */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-[#737373] uppercase tracking-wide font-medium">Response</label>
            {duration !== null && (
              <span className="text-xs text-[#8a6a2f] font-mono font-medium">{duration}ms</span>
            )}
          </div>
          <div className="bg-[#171717] rounded-xl overflow-hidden h-72">
            <div className="h-full px-4 py-3 overflow-auto">
              {loading ? (
                <div className="h-full flex flex-col items-center justify-center gap-3">
                  <div className="w-6 h-6 border-2 border-[#404040] border-t-[#8a6a2f] rounded-full animate-spin" />
                  <span className="text-xs text-[#737373]">Calculating with QuantLib...</span>
                </div>
              ) : error ? (
                <div className="h-full flex items-center justify-center">
                  <p className="text-red-400 text-sm">⚠ {error}</p>
                </div>
              ) : response ? (
                <pre className="text-xs font-mono text-[#a3e635] whitespace-pre-wrap">{response}</pre>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <p className="text-[#737373] text-sm">Click "Run Pricing" to see results</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="mt-4 flex items-center justify-between">
        <p className="text-xs text-[#a3a3a3]">{selectedProduct.description}</p>
        <button
          onClick={runPricing}
          disabled={loading}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#0a0a0a] text-white text-sm font-medium rounded-lg hover:bg-[#262626] disabled:opacity-50 transition-all shadow-sm hover:shadow-md"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {loading ? 'Pricing...' : 'Run Pricing'}
        </button>
      </div>
    </div>
  );
}
